import {
  BodyText,
  Card,
  DetailText,
  Flex,
  Heading,
} from '@utilitywarehouse/hearth-react';
import clsx from 'clsx';

import {
  SlideHeading,
  Terms,
} from '@components/PresentationRebrand/ContentSlide/slides/shared';
import { useBreakpoints } from '@utils/useBreakpoints.ts';

import './styles.scss';

type StepColour = 'pig' | 'energy' | 'broadband' | 'insurance';

type Step = {
  colour: StepColour;
  title: string;
  text: string;
  alignTop?: boolean;
};

const STEPS: Step[] = [
  {
    colour: 'pig',
    title: 'Order your Cashback Card',
    text: " - it's free for three months, then only £3 a month.",
    alignTop: true,
  },
  {
    colour: 'energy',
    title: 'Top it up',
    text: ' - ready to add to Apple Pay or Google Wallet.',
  },
  {
    colour: 'broadband',
    title: 'Go shopping',
    text: ' - groceries, restaurants, streaming, treats.',
  },
  {
    colour: 'insurance',
    title: 'Shrink your bill',
    text: ' - get up to 15% cashback every time you shop, which then comes straight off your UW bill. You can earn up to 15% off from our cashback retail partners and 1% everywhere else.',
    alignTop: true,
  },
];

type Logo = {
  id: string;
  name: string;
  src: string;
};

const logo = (id: string, name: string): Logo => ({
  id,
  name,
  src: `/Presentation/CashbackCard/${id}.png`,
});

const LOGOS: Logo[] = [
  logo('sainsburys', "Sainsbury's"),
  logo('pizza-express', 'Pizza Express'),
  logo('amazon-prime', 'Amazon Prime'),
  logo('dobbies', 'Dobbies'),
  logo('boots', 'Boots'),
  logo('bandq', 'B&Q'),
  logo('netflix', 'Netflix'),
  logo('halfords', 'Halfords'),
  logo('new-look', 'New Look'),
  logo('primark', 'Primark'),
  logo('argos', 'Argos'),
  logo('cineworld', 'Cineworld'),
];

const MOBILE_LOGO_IDS = ['sainsburys', 'netflix', 'amazon-prime', 'boots'];

export const CashbackCard = () => {
  const { breakpointsDown } = useBreakpoints();
  const isMobile = breakpointsDown.sm;
  const isDesktop = !breakpointsDown.md;

  const logos = isMobile
    ? LOGOS.filter(({ id }) => MOBILE_LOGO_IDS.includes(id)).sort(
        (a, b) => MOBILE_LOGO_IDS.indexOf(a.id) - MOBILE_LOGO_IDS.indexOf(b.id)
      )
    : LOGOS;

  const brandGrid = (
    <div className="cashback-card__brands">
      {logos.map(({ id, name, src }) => (
        <Card
          key={id}
          variant="emphasis"
          shadowColor="functional"
          colorScheme="neutralStrong"
          className="cashback-card__logo-card"
          paddingNone
        >
          <img src={src} alt={name} className="cashback-card__logo" />
        </Card>
      ))}
      <Heading size={isMobile ? 'md' : 'xl'} asChild>
        <p className="cashback-card__more">and over 50 more...</p>
      </Heading>
    </div>
  );

  return (
    <Flex direction="column" gap="200" className="cashback-card">
      <div className="cashback-card__content">
        <div className="cashback-card__main">
          <div className="cashback-card__intro">
            <SlideHeading>
              Shrink your bills by going shopping... Yes really!
            </SlideHeading>
            <Heading size={isMobile ? 'sm' : 'md'} asChild>
              <p>
                It's the ace up our sleeve, the UW Cashback Card. Here's how it
                works:
              </p>
            </Heading>
          </div>

          <ol className="cashback-card__steps">
            {STEPS.map(({ colour, title, text, alignTop }, index) => (
              <li
                key={title}
                className={clsx(
                  'cashback-card__step',
                  alignTop && 'cashback-card__step--top'
                )}
              >
                <DetailText
                  size="xl"
                  asChild
                  className={clsx(
                    'cashback-card__number',
                    `cashback-card__number--${colour}`
                  )}
                >
                  <span>{String(index + 1).padStart(2, '0')}</span>
                </DetailText>
                <BodyText size={{ mobile: 'md', tablet: 'lg' }}>
                  <strong>{title}</strong>
                  {text}
                </BodyText>
              </li>
            ))}
          </ol>

          {!isDesktop && brandGrid}
        </div>

        {isDesktop && brandGrid}
      </div>

      <Terms />
    </Flex>
  );
};
